import React from "react";
import { Select, Value, Option, OptionsT } from "baseui/select";
import { css } from "@emotion/react";
import theme from "@styles/theme";

interface SelectUIProps {
  placeholder?: string;
  onClose?: () => void;
  labelKey?: string;
  valueKey?: string;
  clearable?: boolean;
  searchable?: boolean;
  backspaceRemoves?: boolean;
  type?: "select" | "search" | undefined;
  getOptionLabel?: ({ option }: { option?: Option | undefined }) => JSX.Element;
  maxDropdownHeight?: string;
  autoFocus?: boolean;
  size?: "compact" | "mini" | "large" | "default";
}

interface SelectProps {
  options: { id: string | number; label: string }[] | Option[] | OptionsT;
  value: Value | undefined;
  onChange: (params: any) => void;
  props?: SelectUIProps;
  disabled?: boolean;
  placeholder?: string;
  containerStyle?: React.CSSProperties;
  multi?: boolean;
  overrides?: object;
  cleanable?: boolean;
  openInModal?: boolean;
  label?: string;
  helperText?: string;
  required?: boolean;
}

const Selector: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  props,
  placeholder,
  containerStyle,
  multi = false,
  overrides,
  cleanable,
  openInModal = false,
  label,
  helperText,
  required = false,
  disabled = false,
}) => {
  return (
    <div css={styles.wrapper}>
      {label && (
        <label
          css={[
            containerStyle?.marginTop && {
              marginTop: containerStyle?.marginTop,
            },
            {
              marginBottom: "2px",
            },
          ]}>
          {label}
          {required && <span css={{ color: theme.colors.negative400 }}>*</span>}
        </label>
      )}
      <Select
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        {...props}
        multi={multi}
        closeOnSelect={!multi}
        disabled={disabled}
        overrides={
          overrides
            ? overrides
            : {
                Tag: {
                  props: {
                    overrides: {
                      Root: {
                        style: () => ({
                          backgroundColor: "white",
                          borderTopWidth: "1px",
                          borderRightWidth: "1px",
                          borderBottomWidth: "1px",
                          borderLeftWidth: "1px",
                          borderTopStyle: "solid",
                          borderRightStyle: "solid",
                          borderBottomStyle: "solid",
                          borderLeftStyle: "solid",
                          borderTopColor: theme.colors.buttonDisabledFill,
                          borderRightColor: theme.colors.buttonDisabledFill,
                          borderBottomColor: theme.colors.buttonDisabledFill,
                          borderLeftColor: theme.colors.buttonDisabledFill,
                        }),
                      },
                      Text: {
                        style: () => ({
                          color: "black",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "1.15rem",
                        }),
                      },
                      Action: {
                        style: () => ({ display: "none" }),
                      },
                    },
                  },
                },
                ControlContainer: {
                  style: ({ $theme }) => ({
                    width: containerStyle?.width && `${containerStyle?.width}`,
                    ...(containerStyle?.borderRadius && {
                      borderTopLeftRadius: containerStyle.borderRadius,
                      borderTopRightRadius: containerStyle.borderRadius,
                      borderBottomLeftRadius: containerStyle.borderRadius,
                      borderBottomRightRadius: containerStyle.borderRadius,
                    }),
                    padding:
                      containerStyle?.padding && `${containerStyle?.padding}`,
                    fontWeight:
                      containerStyle?.fontWeight &&
                      `${containerStyle?.fontWeight}`,
                    backgroundColor: $theme.colors.white,
                    borderTopWidth: "1px",
                    borderRightWidth: "1px",
                    borderBottomWidth: "1px",
                    borderLeftWidth: "1px",
                    borderTopStyle: "solid",
                    borderRightStyle: "solid",
                    borderBottomStyle: "solid",
                    borderLeftStyle: "solid",
                    borderTopColor: $theme.colors.contentInverseSecondary,
                    borderRightColor: $theme.colors.contentInverseSecondary,
                    borderBottomColor: $theme.colors.contentInverseSecondary,
                    borderLeftColor: $theme.colors.contentInverseSecondary,
                  }),
                },
                ClearIcon: {
                  props: {
                    overrides: {
                      Svg: {
                        style: () => ({
                          display: !!cleanable ? "block" : "none",
                        }),
                      },
                    },
                  },
                },
                Dropdown: {
                  style: ({ $theme }) => ({
                    backgroundColor: $theme.colors.white,
                  }),
                },
                Root: {
                  style: () => ({
                    overflow: "hidden !important",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingBottom: containerStyle?.paddingBottom,
                    width: containerStyle?.width && `${containerStyle?.width}`,
                  }),
                },
                ...(openInModal
                  ? {
                      Popover: {
                        props: {
                          overrides: {
                            Body: {
                              style: () => ({
                                zIndex: 9999,
                              }),
                            },
                          },
                        },
                      },
                    }
                  : {}),
              }
        }
      />
      {helperText && <span css={styles.helper}>{helperText}</span>}
    </div>
  );
};
const styles = {
  label: css({
    paddingBottom: "10px",
  }),
  wrapper: css({
    display: "flex",
    flexDirection: "column",
  }),
  helper: css({
    fontSize: ".8rem",
    color: theme.colors.mono600,
  }),
};

export default Selector;
